import React from 'react';
import { select } from '~/lib/select';
import { useNextSequence } from '~/training/useNextSequence';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { useUnlockAudio } from '~/ui/video-player/MediaPlayerProvider';
import { Sequence, TrainingState, useTrainingStore } from '~/training/state';
import { usePrevSequence } from '~/training/usePrevSequence';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { useQueryParams } from '~/lib/useQuery';
import LightButton from '~/editor/buttons/LightButton';
import styled from '@xstyled/styled-components';
import ArrowIcon from '~/assets/button/arrow.svg';
import DesktopIcon from '~/assets/button/desktop.svg';
import PreviewModeArrowIcon from '~/assets/icons/preview-mode-arrow.svg';
import { t } from 'ttag';
import { useEditorStore } from '~/shell/Store';

/** Legacy editor plaque — keep unchanged for content-authoring preview */
const EditorSkipSection = styled.div`
  width: 240px;
  height: 58px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 3px 3px 5px #0000004f;
  left: 56px;
  top: 20px;
  position: absolute;
  z-index: hud;
  padding: 10px;
  display: flex;
  justify-content: space-between;

  button.arrow {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background-color: #f8f1ff;
    color: var(--color-accent-dk);
    padding: 0;

    svg {
      margin-left: auto;
      margin-right: auto;
      path {
        fill: var(--color-accent-dk) !important;
      }
    }
  }

  button.arrow:hover {
    svg {
      path {
        fill: #fff !important;
      }
    }
  }

  button.arrow-left {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
  }

  button.arrow-right {
    border-left: 1px solid #e0e0e0;
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;

    svg {
      transform: rotate(180deg);
    }
  }

  button.arrow:hover {
    background-color: var(--color-accent-dk) !important;
  }

  .devices {
    button {
      width: 38px;
      height: 38px;
      border-radius: 10px;
      background-color: var(--color-accent-dk) !important;
      padding: 0;
      svg {
        margin-left: auto;
        margin-right: auto;
      }
    }
  }
  .navBtns {
    margin-left: 10px;
    button {
      &:disabled {
        background-color: #e0e0e0 !important;
        svg {
          path {
            fill: grey !important;
          }
        }
      }
    }
  }
  .preview-mode {
    color: #000;
    font-family: Raleway;
    font-size: 12px;
    font-weight: 800;
    line-height: 18px;
    text-align: left;
    flex: 1;
    padding-left: 10px;
  }
`;

/**
 * Figma EDS-CAT 598:6488 — learner / standalone preview only.
 * Width grows with translated "PREVIEW MODE" label.
 */
const PreviewModeBar = styled.div`
  position: absolute;
  z-index: hud;
  /* Align with EDS burger (38px @ left 8px / top 20px) + 8px gap */
  left: 54px;
  top: 20px;
  display: flex;
  align-items: center;
  height: 38px;
  max-width: calc(100vw - 62px);
  box-sizing: border-box;
  padding: 0 12px 0 4px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow:
    0px -2px 4px rgba(0, 0, 0, 0.05),
    0px 15px 20px rgba(0, 0, 0, 0.05),
    0px 30px 30px rgba(0, 0, 0, 0.03);
`;

const PreviewNavGroup = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 32px;
`;

const PreviewNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  border: none;
  cursor: pointer;
  background: #f8f1ff;
  color: var(--color-accent-dk);
  line-height: 0;

  svg {
    width: 12px;
    height: 9px;
    display: block;
    flex-shrink: 0;
  }

  &.preview-arrow-prev {
    border-radius: 8px 0 0 8px;
    /* Figma: left chevron is mirrored right-arrow */
    svg {
      transform: scaleX(-1);
    }
  }

  &.preview-arrow-next {
    border-radius: 0 8px 8px 0;
  }

  &:hover:not(:disabled) {
    background: color-mix(in srgb, #f8f1ff 70%, var(--color-accent-dk));
  }

  &:active:not(:disabled) {
    background: color-mix(in srgb, #f8f1ff 55%, var(--color-accent-dk));
  }

  &:focus {
    outline: 2px solid var(--color-accent-dk);
    outline-offset: 2px;
    z-index: 1;
  }

  &:disabled {
    cursor: not-allowed;
    color: #9b9b9b;
    background: #f0f0f0;
  }
`;

const PreviewNavDivider = styled.span`
  display: block;
  width: 1px;
  height: 32px;
  flex-shrink: 0;
  background: #e7d6f7;
`;

const PreviewModeLabel = styled.span`
  margin-left: 8px;
  color: #2e2e2e;
  font-family: ralewayBold;
  font-size: 14px;
  line-height: 1;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-variant-numeric: lining-nums proportional-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const selector = (s: TrainingState) =>
  select(s, 'skip', 'items', 'activeItemPosition');
const sequenceSelector = (s: SequenceStore) => select(s, 'clearBlocker');

export const SkipButtons = () => {
  const { skip, items, activeItemPosition } = useTrainingStore(selector);
  const { clearBlocker } = useSequenceStore(sequenceSelector);
  const { editorMode } = useEditorStore();
  const params = useQueryParams();

  const next = useNextSequence();
  const prev = usePrevSequence();

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;
  const unlockAudio = useUnlockAudio();

  const goNext = () => {
    // Navigation below is deferred, so this has to run first to still count as
    // part of the gesture.
    unlockAudio();
    clearBlocker();
    if (globalPlayer) {
      try {
        globalPlayer?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }
    }
    setTimeout(() => next(), 100);
  };

  const goPrev = () => {
    unlockAudio();
    clearBlocker();
    if (globalPlayer) {
      try {
        globalPlayer?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }
    }
    setTimeout(() => prev(), 100);
  };

  if (!skip || params.get('removeAllButtons') === 'true') {
    return null;
  }

  const allSequences = items.filter(
    (item): item is Sequence => item.kind == 'sequence'
  );
  const atFirst = allSequences[0]?.position == activeItemPosition;
  const atLast =
    allSequences[allSequences.length - 1]?.position == activeItemPosition;
  const previewModeLabel = t`PREVIEW MODE`;

  if (editorMode) {
    return (
      <EditorSkipSection>
        <div className="devices">
          <LightButton className="">
            <DesktopIcon />
          </LightButton>
        </div>
        <div className="navBtns">
          <LightButton
            onClick={goPrev}
            className="arrow arrow-left"
            disabled={atFirst}
          >
            <ArrowIcon />
          </LightButton>
          <LightButton
            onClick={goNext}
            className="arrow arrow-right"
            disabled={atLast}
          >
            <ArrowIcon />
          </LightButton>
        </div>
        <div className="preview-mode">{previewModeLabel}</div>
      </EditorSkipSection>
    );
  }

  return (
    <PreviewModeBar role="region" aria-label={previewModeLabel}>
      <PreviewNavGroup role="group" aria-label={t`Preview navigation`}>
        <PreviewNavButton
          type="button"
          className="preview-arrow-prev"
          onClick={goPrev}
          disabled={atFirst}
          aria-label={t`Previous slide`}
        >
          <PreviewModeArrowIcon aria-hidden />
        </PreviewNavButton>
        <PreviewNavDivider aria-hidden />
        <PreviewNavButton
          type="button"
          className="preview-arrow-next"
          onClick={goNext}
          disabled={atLast}
          aria-label={t`Next slide`}
        >
          <PreviewModeArrowIcon aria-hidden />
        </PreviewNavButton>
      </PreviewNavGroup>
      <PreviewModeLabel>{previewModeLabel}</PreviewModeLabel>
    </PreviewModeBar>
  );
};
